import axios from 'axios';
import { isEmpty } from 'lodash';
import { STATUS_PRECONDITION_REQUIRED } from '../../axios/axios.constants';
import { AxiosService } from '../../axios/axios.service';
import { axiosInstance } from '../../axios/axios.singleton';
import { tAxiosCustomConfig } from '../../axios/axios.types';
import { tProfileForm } from '../../pages/Settings/components/Profile/Profile.types';
import { sentry } from '../../sentry/sentry.singleton';
import {
  tAuthResponse,
  tSignIn,
  tSignUpRequest,
  tUpdatedUser,
  tUpdateNameAndPhoneProfile,
  tUpdateUserResponse,
  WorkspaceUserUpdatePayload,
} from './User.types';

export const ENDPOINT_WORKSPACE_SERVICE = 'workspaces';
const STORAGE_MANAGEMENT_URL = import.meta.env.VITE_STORAGE_MANAGEMENT_URL as string;
const storageDomain = 'leadsales-storage.s3.amazonaws.com';
const newFileDomain = import.meta.env.VITE_BASE_FILES_URL;

/**
 * Reads one ahooks-serialized string out of localStorage. The parse contract
 * (JSON.parse, non-empty-string check, swallow-on-corrupt) must match the
 * ahooks serializer exactly — the cross-account reconciliation guard depends
 * on it, so it lives in one place.
 */
function readStoredString(key: string): string | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'string' && parsed !== '' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reads the workspace the user is currently working in from localStorage
 * (written by WorkspaceProvider via ahooks' JSON serializer). Used as an
 * attribution hint on sign-in and session-touch; the backend validates
 * membership and ignores invalid hints.
 */
export function getStoredWorkspaceId(): string | undefined {
  return readStoredString('currentWorkspaceId');
}

/**
 * Reads the persisted user identity mirror from localStorage (written by
 * UserProvider via ahooks' JSON serializer). BUG-801: read straight from
 * storage — not from React state — so the cross-account reconciliation guard
 * compares against what the LAST login on this browser profile wrote, free of
 * stale-closure values.
 */
export function getStoredUserId(): string | undefined {
  return readStoredString('currentUserId');
}

/**
 * Client-side once-per-UTC-day gate key for the boot session touch. One key
 * per user+workspace; the value holds the last touched UTC date so keys do
 * not accumulate. The server-side dedup remains authoritative.
 */
export function buildSessionTouchGate(
  userUuid: string,
  workspaceId?: string,
): { key: string; today: string } {
  return {
    key: `sessionTouch:${userUuid}:${workspaceId ?? 'global'}`,
    today: new Date().toISOString().slice(0, 10),
  };
}

/**
 * BUG-800: sign-in and sign-up refuse an address with a pending invitation with
 * a 428, which the page turns into the next step for that person. An answer,
 * not an incident: every wrong-door attempt meets it, and the activation page's
 * sign-in retry can meet it five times more.
 */
const INVITATION_REFUSAL_STATUSES = [STATUS_PRECONDITION_REQUIRED] as const;

export const UserService = {
  signIn: async function (): Promise<tAuthResponse> {
    try {
      const deviceName = navigator.userAgent || 'Unknown';
      const workspaceHint = getStoredWorkspaceId();

      const body = {
        device_type: 'Desktop',
        device_name: deviceName,
        ...(workspaceHint ? { workspace_id: workspaceHint } : {}),
      };
      const response = await AxiosService.post<tSignIn, tAuthResponse>(
        '/auth/sign-in',
        body,
        { skipSentryCaptureForStatuses: INVITATION_REFUSAL_STATUSES },
      );

      sentry.setUser({
        id: response.data.authId,
        email: response.data.email,
        username: response.data.name,
      });

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Records the app boot as a passive session row (source 'boot' upstream).
   * Strictly fire-and-forget: skips the 401 localStorage-wipe interceptor
   * AND Sentry capture — during backend deploy skew every boot would
   * otherwise emit DAU-sized alert noise. Callers must swallow rejections.
   */
  sessionTouch: async function (params: {
    userUuid: string;
    workspaceId?: string;
  }): Promise<void> {
    const body = {
      user_uuid: params.userUuid,
      device_type: 'Desktop',
      device_name: navigator.userAgent || 'Unknown',
      ...(params.workspaceId ? { workspace_id: params.workspaceId } : {}),
    };

    await AxiosService.post('/auth/session-touch', body, {
      skipAutoRedirect: true,
      skipSentryCapture: true,
    });
  },

  signUp: async function (request: tSignUpRequest): Promise<tAuthResponse> {
    try {
      const response = await AxiosService.post<tSignUpRequest, tAuthResponse>(
        '/auth/sign-up',
        request,
        { skipSentryCaptureForStatuses: INVITATION_REFUSAL_STATUSES },
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  updateUserProfile: async (
    profileData: tProfileForm,
    userId: string,
  ): Promise<tUpdatedUser> => {
    const updateProfileData: WorkspaceUserUpdatePayload = {
      name: profileData.name,
      lastName: profileData.lastName,
      phoneNumber:
        profileData.phone === undefined ? null : `${profileData.phone}`,
      profilePicture: profileData.profilePicture,
    };

    const config = {
      headers: {},
      isLegacy: true,
    };

    try {
      const updateResponse = await AxiosService.patchById<
        WorkspaceUserUpdatePayload,
        tUpdateUserResponse
      >(
        `${ENDPOINT_WORKSPACE_SERVICE}/user`,
        userId,
        updateProfileData,
        config,
      );

      if (isEmpty(updateResponse.data)) {
        throw new Error('No data found');
      }
      return Promise.resolve(updateResponse.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  uploadNewS3File: async (
    file: File,
    workspaceId: string,
  ): Promise<string | null> => {
    const fileSize = file.size / 1024 / 1024;
    const mimeType = file.type;
    const rawExtension = file.name.substring(file.name.lastIndexOf('.') + 1);
    const extension = rawExtension.startsWith('.') ? rawExtension : `.${rawExtension}`;

    if (fileSize > 100) {
      throw new Error('size error');
    }

    try {
      // Presign goes through the authenticated instance like the chat upload
      // (BUG-1142), so the gateway can enforce jwt-auth on it. The S3 PUT stays
      // on bare axios: an extra header would break the presigned signature.
      const presignConfig: tAxiosCustomConfig = { skipAutoRedirect: true };
      const presignResponse = await axiosInstance.post(
        `${STORAGE_MANAGEMENT_URL}/files/${workspaceId}/presign`,
        { extension, contentType: mimeType },
        presignConfig,
      );

      const uploadUrl = presignResponse.data?.data?.uploadUrl;
      if (!uploadUrl) throw new Error('Presigned URL missing from storage service response');

      await axios.put(uploadUrl, file, { headers: { 'Content-Type': mimeType } });

      const finalURL = uploadUrl.split('?')[0].replace(storageDomain, newFileDomain);
      return Promise.resolve(finalURL);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  updateUserNameAndPhone: async (
    profileData: tUpdateNameAndPhoneProfile,
    userId: string,
  ): Promise<tUpdatedUser> => {
    const updateProfileData: WorkspaceUserUpdatePayload = {
      name: profileData.firstName,
      lastName: profileData.lastName,
      phoneNumber: `${profileData.phoneNumber}`,
      profilePicture: profileData.profilePicture,
    };

    const config = {
      headers: {},
      isLegacy: true,
    };

    try {
      const updateResponse = await AxiosService.patchById<
        WorkspaceUserUpdatePayload,
        tUpdateUserResponse
      >(
        `${ENDPOINT_WORKSPACE_SERVICE}/user`,
        userId,
        updateProfileData,
        config,
      );

      if (isEmpty(updateResponse.data)) {
        throw new Error('No data found');
      }
      return Promise.resolve(updateResponse.data);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  signOut: async function (): Promise<tAuthResponse> {
    try {
      const body = {
        device_type: 'Desktop',
      };
      const response = await AxiosService.post<tSignIn, tAuthResponse>(
        '/auth/log-out',
        body,
      );
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  validateActivationToken: async function (
    token: string,
  ): Promise<Record<string, unknown>> {
    try {
      const response = await AxiosService.get<Record<string, unknown>>(
        `/user/activate?token=${token}`,
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
